import { Backdrop, Button, Card, Divider, Grid, Hidden, makeStyles, Paper, Typography } from "@material-ui/core";
import { CheckCircleOutline, RemoveCircleOutline } from "@material-ui/icons";
import Add from "@material-ui/icons/Add";
import moment from "moment";
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { AuthContext } from "../../Auth/AuthContextProvider";
import { BirthSexOptions, FeeStatus, IdentificationStatus, RegistrationType } from "../../Base/AdoptionConstants";
import { LightTooltip } from "../../Base/Components/LightTooltip";
import { getRegistrationsForUser } from "../../Base/Services/RegistrationService";
import CertificateInformationDialog from "./CertificateInformationDialog";
import MatchInformationDialog from "./MatchInformationDialog";
import NewRegistrationDialog from "./NewRegistrationDialog";
import ViewUploadHealthDocumentsDialog from "./ViewUploadHealthDocumentsDialog";
import ViewUploadIdentificationDocumentsDialog from "./ViewUploadIdentificationDocumentsDialog";

const useStyles = makeStyles((theme) => ({
  root: {
    margin: theme.spacing(3),
    marginLeft: "auto",
    marginRight: "auto",
    width: 800,
    maxWidth: "90%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    paddingBottom: theme.spacing(3),
  },
  AdoptionRegistrationButton: {
    float: "right",
    marginTop: theme.spacing(1),
  },
  buttonIcon: {
    marginRight: theme.spacing(1),
  },
  noRegistrationsCard: {
    margin: theme.spacing(1),
    padding: theme.spacing(1),
    maxWidth: 600,
    marginLeft: "auto",
    marginRight: "auto",
  },
  backdrop: {
    zIndex: theme.zIndex.modal,
  },
  buttonOverBackdrop: {
    zIndex: theme.zIndex.modal + 1,
  },
}));

export default function MyRegistrations() {
  const classes = useStyles();
  const location = useLocation();
  const authContext = React.useContext(AuthContext);
  const [adoptionRegistrations, setAdoptionRegistrations] = React.useState(null);
  const [showNewRegistrationDialog, setShowNewRegistrationDialog] = React.useState(false);
  const [viewUploadHealthDocumentsRegistrationId, setViewUploadHealthDocumentsRegistrationId] = React.useState(null);
  const [showCertificateInformationDialog, setShowCertificateInformationDialog] = React.useState(null);
  const [showMatchInformationDialog, setShowMatchInformationDialog] = React.useState(null);
  const [viewUploadIdentificationDocumentsRegistration, setViewUploadIdentificationDocumentsRegistration] =
    React.useState(null);
  const [medicalHistoryPromptRegistration, setMedicalHistoryPromptRegistration] = React.useState(null);
  const hasAdultAdopteeRegistration = React.useMemo(
    () =>
      adoptionRegistrations &&
      adoptionRegistrations.some((registration) => registration.registrationType === RegistrationType.ADOPTEE.value),
    [adoptionRegistrations]
  );

  function loadAdoptionRegistrations() {
    return getRegistrationsForUser(authContext.userInfo.userId).then((resp) => {
      setAdoptionRegistrations(resp.data);
    });
  }

  React.useEffect(loadAdoptionRegistrations, [authContext.userInfo]);

  React.useEffect(() => {
    if (location.state?.newlySavedRegistrationId && adoptionRegistrations && adoptionRegistrations.length) {
      const newlySavedRegistration = adoptionRegistrations.find(
        (registration) => registration.registrationId === location.state.newlySavedRegistrationId
      );
      // If the newly saved registration selected to share medical & they haven't saved any documents yet, prompt them to!
      if (
        newlySavedRegistration.shareMedicalHistory &&
        (!newlySavedRegistration.documents || newlySavedRegistration.documents.length === 0)
      ) {
        setMedicalHistoryPromptRegistration(newlySavedRegistration);
      }
    }
  }, [location.state, adoptionRegistrations]);

  React.useEffect(() => {
    if (medicalHistoryPromptRegistration) {
      document
        .getElementById("registration-" + medicalHistoryPromptRegistration.registrationId)
        .scrollIntoView({ behavior: "smooth" });
    }
  }, [medicalHistoryPromptRegistration]);

  return (
    <div className={classes.root}>
      <Typography variant="h2" style={{ marginBottom: 24 }}>
        My Registrations
      </Typography>
      {adoptionRegistrations &&
        adoptionRegistrations.length > 0 &&
        adoptionRegistrations.map((adoptionRegistration, i) => (
          <Paper
            style={{ marginBottom: 16 }}
            elevation={3}
            key={i}
            id={"registration-" + adoptionRegistration.registrationId}
            role="main"
          >
            <Grid container justify="space-between" alignItems="center" style={{ paddingLeft: 16, paddingRight: 16 }}>
              <Grid item>
                <Typography variant="h3" className="h3-header">
                  Registration ID: <strong id={"registration-id-" + i}>{adoptionRegistration.userFriendlyId}</strong>
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant="body1">{RegistrationType[adoptionRegistration.registrationType].label}</Typography>
              </Grid>
            </Grid>
            <Divider />
            <Grid container>
              <Grid
                item
                xs={12}
                sm={2}
                style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: 8 }}
              >
                <Typography variant="caption" align="center">
                  Registered: {moment(adoptionRegistration.createdDate).format("MM/DD/YYYY")}
                </Typography>
                {adoptionRegistration.registrationStatus === "MATCHED" ? (
                  <CheckCircleOutline style={{ fontSize: 80 }} color="primary" />
                ) : (
                  <RemoveCircleOutline style={{ fontSize: 80 }} color="disabled" />
                )}

                <Typography variant="caption" align="center">
                  {adoptionRegistration.registrationStatus === "MATCHED" ? "Match found" : "Match not yet found"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} style={{ display: "flex" }}>
                <Hidden xsDown>
                  <Divider orientation="vertical" flexItem />
                </Hidden>
                <Grid container direction="column" style={{ padding: 8, paddingLeft: 16 }}>
                  <label style={{ fontWeight: 500, fontSize: 14 }}>Adoptee Birth Date</label>
                  {adoptionRegistration.birthDate
                    ? moment(adoptionRegistration.birthDate).format("LL")
                    : `${moment(adoptionRegistration.birthDateRangeStart).format("LL")} - ${moment(
                        adoptionRegistration.birthDateRangeEnd
                      ).format("LL")}`}
                  <label style={{ fontWeight: 500, fontSize: 14, marginTop: 8 }}>Adoptee Place of Birth</label>
                  <Typography
                    variant="body1"
                    style={{
                      maxWidth: "100%",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {!adoptionRegistration.birthHospital || adoptionRegistration.birthHospital === "Unknown / Other"
                      ? !adoptionRegistration.birthCity || adoptionRegistration.birthCity === "Unknown / Other"
                        ? adoptionRegistration.otherBirthLocation
                        : adoptionRegistration.birthCity
                      : adoptionRegistration.birthHospital}
                  </Typography>
                  <label style={{ fontWeight: 500, fontSize: 14, marginTop: 8 }}>Adoptee Birth Sex</label>
                  <Typography variant="body1">
                    {adoptionRegistration.birthSex
                      ? BirthSexOptions.find((sex) => sex.value === adoptionRegistration.birthSex).label
                      : "-"}
                  </Typography>
                </Grid>
              </Grid>
              <Grid
                item
                sm={4}
                container
                direction="column"
                justify="space-around"
                alignItems="center"
                spacing={1}
                style={{ padding: 16 }}
              >
                {adoptionRegistration.registrationStatus === "MATCHED" && (
                  <Grid item>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setShowMatchInformationDialog(adoptionRegistration.registrationId);
                      }}
                    >
                      Match Information
                    </Button>
                  </Grid>
                )}
                {adoptionRegistration.registrationType === RegistrationType.ADOPTEE.value &&
                  adoptionRegistration.identificationStatus === IdentificationStatus.VERIFIED.value &&
                  adoptionRegistration.registrationStatus !== "MATCHED" &&
                  (adoptionRegistration.feeStatus === FeeStatus.FEE_PAID.value ||
                    adoptionRegistration.feeStatus === FeeStatus.FEE_WAIVED.value) && (
                    <Grid item>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          setShowCertificateInformationDialog(adoptionRegistration.registrationId);
                        }}
                      >
                        View Certificate
                      </Button>
                    </Grid>
                  )}
                {adoptionRegistration.registrationType === RegistrationType.BIRTH_PARENT.value &&
                  adoptionRegistration.shareMedicalHistory === true && (
                    <Grid item>
                      <LightTooltip
                        open={Boolean(
                          medicalHistoryPromptRegistration?.registrationId === adoptionRegistration.registrationId
                        )}
                        title={
                          "Now that you are registered, you can share documents related to your medical history here"
                        }
                        PopperProps={{ style: { fontSize: "100%" } }}
                        placement="top"
                        className={
                          Boolean(
                            medicalHistoryPromptRegistration?.registrationId === adoptionRegistration.registrationId
                          )
                            ? classes.buttonOverBackdrop
                            : ""
                        }
                        arrow
                      >
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            setMedicalHistoryPromptRegistration(null);
                            setViewUploadHealthDocumentsRegistrationId(adoptionRegistration.registrationId);
                          }}
                          className={
                            Boolean(
                              medicalHistoryPromptRegistration?.registrationId === adoptionRegistration.registrationId
                            )
                              ? classes.buttonOverBackdrop
                              : ""
                          }
                        >
                          Documents
                        </Button>
                      </LightTooltip>
                    </Grid>
                  )}
                {adoptionRegistration.identificationStatus !== IdentificationStatus.VERIFIED.value && (
                  <Grid item>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setMedicalHistoryPromptRegistration(null);
                        setViewUploadIdentificationDocumentsRegistration(adoptionRegistration);
                      }}
                    >
                      Verify Identity
                    </Button>
                  </Grid>
                )}
                <Grid item>
                  <Button
                    variant="contained"
                    component={Link}
                    style={{ textAlign: "center" }}
                    to={{ pathname: "/registration/" + adoptionRegistration.userFriendlyId }}
                  >
                    View/Edit Details
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        ))}

      {adoptionRegistrations && adoptionRegistrations.length === 0 && (
        <Card className={classes.noRegistrationsCard}>
          <Typography>No registrations found</Typography>
        </Card>
      )}
      <span>
        <Button
          color="primary"
          variant="contained"
          className={classes.AdoptionRegistrationButton}
          size="large"
          onClick={() => setShowNewRegistrationDialog(true)}
        >
          <Add className={classes.buttonIcon} /> Add new registration
        </Button>
      </span>
      <Backdrop
        id="popover-backdrop"
        open={Boolean(medicalHistoryPromptRegistration)}
        elevation={4}
        className={classes.backdrop}
        onClick={() => setMedicalHistoryPromptRegistration(null)}
      />

      <NewRegistrationDialog
        open={showNewRegistrationDialog}
        includeAdultAdoptee={!hasAdultAdopteeRegistration}
        onClose={() => setShowNewRegistrationDialog(false)}
        aria-label="Who are you registering as dialog"
      />
      <ViewUploadHealthDocumentsDialog
        open={Boolean(viewUploadHealthDocumentsRegistrationId)}
        registrationId={viewUploadHealthDocumentsRegistrationId}
        onClose={() => setViewUploadHealthDocumentsRegistrationId(null)}
        aria-label="Documents / Medical History Dialog"
      />
      <ViewUploadIdentificationDocumentsDialog
        open={Boolean(viewUploadIdentificationDocumentsRegistration)}
        registration={viewUploadIdentificationDocumentsRegistration}
        onClose={(saveChangeResponses) => {
          // If we have saved changes in array response, reload the registrations
          if (Array.isArray(saveChangeResponses) && saveChangeResponses.length) {
            loadAdoptionRegistrations();
          }
          setViewUploadIdentificationDocumentsRegistration(null);
        }}
        aria-label="Identity Verification Dialog"
      />
      <CertificateInformationDialog
        open={Boolean(showCertificateInformationDialog)}
        registrationId={showCertificateInformationDialog}
        onClose={() => setShowCertificateInformationDialog(null)}
        aria-label="Certificate Information Dialog"
      />
      <MatchInformationDialog
        open={Boolean(showMatchInformationDialog)}
        registrationId={showMatchInformationDialog}
        onClose={() => setShowMatchInformationDialog(null)}
        aria-label="Adoption Match Information Dialog"
      />
    </div>
  );
}
